import { Component, OnInit, signal, ViewChild } from "@angular/core";
import { DataService } from "../../services/data.service";
import { IExtensionService } from "../../models/interfaces.model";
import { AbcCore } from "@barthauer/abc/core";
import { ActivatedRoute, Router } from "@angular/router";
import { LandingPageService } from "./landing-page.service";
import {
  AbcLoginComponentInfo,
  RegisterComponent,
  UserService,
} from "@barthauer/aptis-authentication";
import {
  IUserInfo,
  NotificationMessage,
  Person,
  ToastType,
} from "@barthauer/custom/xmt-models";
import { NotificationService } from "../../services/notification.service";
import { FormGroup } from "@angular/forms";
import {
  delay,
  first,
  Observable,
  OperatorFunction,
  pipe,
  switchMap,
  take,
  tap,
} from "rxjs";
import { NotificationService1 } from "../../services/notification1.service";
import { PersistMode } from "@barthauer/abc/core/data";

@Component({
  selector: "app-landing-page",
  templateUrl: "./landing-page.component.html",
  styleUrls: ["./landing-page.component.scss"],
  standalone: false,
})
export class LandingPageComponent implements OnInit {
  @ViewChild("register", { static: true }) register: RegisterComponent;
  protected user: Person;
  private personId: string;
  protected isSuccess = signal(false);
  protected isSessionExpired = signal(false);
  private invitationTimeStamp: number;
  public translator = this.dataService.res;
  protected currentRoutePath: string;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private abcCore: AbcCore,
    private userService: UserService,
    private landingPageService: LandingPageService,
    public dataService: DataService,
    private notificationService: NotificationService,
    private notificationService1:NotificationService1
  ) {}

  async ngOnInit(): Promise<void> {
    this.currentRoutePath = this.route.routeConfig?.path;
    this.route.paramMap.pipe(take(1)).subscribe((params) => {
      const payload = atob(params.get("id"));
      this.invitationTimeStamp = Number(payload.split("&&&")[1]);
      this.personId = payload.split("&&&")[0];

      if (this.checkSessionExpiration()) {
        this.isSessionExpired.set(true);
        return;
      }

      if (this.personId) {
        try {
          const userInfoParameters: IUserInfo = {
            Id: this.personId,
          };
          this.landingPageService
            .getUserLogin(userInfoParameters)
            .toPromise()
            .then((response) => {
              const extenstionServiceResponse =
                response as unknown as IExtensionService<Person>;
              this.user = extenstionServiceResponse.Data;
            });
        } catch (error) {
          this.abcCore.error.publish(error);
        }
      }
    });
  }

  /**
   * Check if the session is expired based on the invitation timestamp.
   * The session is considered expired if the current date is later than the expiration time.
   * The expiration time is calculated based on the invitation timestamp and the route type.
   * For the registration route, the expiration time is set to 24 hours after the invitation timestamp.
   * For the password reset route, the expiration time is set to 15 minutes after the invitation timestamp.
   * If the invitation timestamp is not available, the session is considered expired.
   * @returns True if the session is expired, otherwise false.
   */
  private checkSessionExpiration(): boolean {
    let expirationTime: number;
    if (!this.invitationTimeStamp) {
      return true;
    }
    const currentDate = new Date().getTime();
    if (this.currentRoutePath === "register/:id") {
      expirationTime = this.invitationTimeStamp + 24 * 60 * 60 * 1000; // +24h
    } else {
      expirationTime = this.invitationTimeStamp + 15 * 60 * 1000; // +15min
    }
    return currentDate > expirationTime;
  }

  /**
   * Handle the user submission for registration or password reset.
   * @param form - The form group containing user input.
   */
  public onUserSubmit(form: FormGroup): void {
    const password = form.value.password;
    const confirmPassword = form.value.confirmPassword;

    if (this.checkSessionExpiration()) {
      this.isSessionExpired.set(true);
      return;
    }

    if (this.isRegisterRoute()) {
      this.handleRegister(password, confirmPassword);
      return;
    }

    if (this.isResetPasswordRoute()) {
      this.handleResetPassword(password);
    }
  }

  /**
   * Handle the user registration process.
   * @param password - The user's chosen password.
   * @param confirmPassword - The confirmation of the user's chosen password.
   */
  private handleRegister(password: string, confirmPassword: string): void {
    const userInfo: IUserInfo = {
      Id: this.user.Id,
    };

    const register$ = this.userService
      .registerUser(this.user.Login, password, confirmPassword)
      .pipe(
        switchMap(() => this.landingPageService.setUserActive(userInfo)),
        switchMap(()=> this.enableEmailNotificationSettings()
      ),
        this.handleSuccessFlow()
      );

    this.subscribeWithNavigation(register$);
  }

  /**
   * Handle the user password reset process.
   * @param password - The user's chosen password.
   */
  private handleResetPassword(password: string): void {
    const resetPassword$ = this.dataService
      .resetPassword(this.user.Login, password)
      .pipe(this.handleSuccessFlow());

    this.subscribeWithNavigation(resetPassword$);
  }

  /**
   * Handle the successful completion of a registration or password reset operation.
   * @returns An operator function that sets the success flag, displays a notification, and navigates to the login page.
   */
  private handleSuccessFlow<T>(): OperatorFunction<T, T> {
    return pipe(
      tap(() => {
        this.isSuccess.set(true);
        this.notificationService.callNotification({
          message: this.dataService.res(NotificationMessage.UserCreatedSuccess),
          type: ToastType.SUCCESS,
        });
      }),
      delay(3000)
    );
  }

  /**
   * Subscribe to an observable and navigate to the login page on success.
   * @param observable$ - The observable to subscribe to.
   */
  private subscribeWithNavigation<T>(observable$: Observable<T>): void {
    observable$.pipe(first()).subscribe({
      next: () => {
        this.router.navigateByUrl(AbcLoginComponentInfo.ROUTE_PATH);
      },
      error: (error) => {
        this.notificationService.callNotification({
          message: this.dataService.res(NotificationMessage.Error),
          type: ToastType.ERROR,
        });
        this.abcCore.error.publish(error);
      },
    });
  }

  /**
   * Check if the current route is the registration route.
   * @returns True if the current route is the registration route, otherwise false.
   */
  private isRegisterRoute(): boolean {
    return this.currentRoutePath.startsWith("register");
  }

  /**
   * Check if the current route is the password reset route.
   * @returns True if the current route is the password reset route, otherwise false.
   */
  private isResetPasswordRoute(): boolean {
    return this.currentRoutePath.startsWith("reset-password");
  }
 private enableEmailNotificationSettings(): Observable<ArrayBuffer> {
  return this.landingPageService.enableEmailNotificationSettings(
    this.user.Id
  );
}

}
